/**
 * In-app Back (owner 2026-07-26): the mobile edge-swipe / Android back
 * gesture / desktop Back button must navigate WITHIN the portal (Customise →
 * Photos, close the cart tab/drawer, Cart → Size & Paper) instead of leaving
 * for the previous website page.
 *
 * Mechanism: the flow's visible screen is a snapshot {step, tab, cart}.
 * Every snapshot change the customer makes pushes a history entry carrying it
 * (pushState with NO url change — the app-proxy address and ?resume/?share
 * handling stay untouched). The popstate listener applies the landed entry's
 * snapshot back into the store, so Back retraces the customer's screens
 * exactly like a routed SPA — and forward-swipe re-advances. Once history
 * bottoms out at the entry the customer ARRIVED on, Back leaves the site as
 * normal: the customer is never trapped.
 *
 * react-router coexistence: pushed entries spread the existing history.state
 * (preserving the router's own fields), and a same-URL POP re-renders the
 * identical route, so the router never fights the store. The one interaction
 * — OrderingFlow's ?resume/?share cleanup REPLACING the top entry — can
 * strip our marker from a single entry; popstate then falls back to the
 * arrival snapshot.
 */
import { useEffect, useRef } from 'react';
import type { CfgStep } from '../components/sizepaper/cfgSteps';
import { useStore } from '../store/store';

export interface NavSnapshot {
  step: number;
  tab: 'photos' | 'configure' | 'cart';
  cart: boolean;
  /** Mobile stepped-Customise position (owner 2026-07-27) — a customer on
   *  step 3 who swipes back must land on step 2, not leave the flow. Only the
   *  stepped mobile UI ever writes it, so desktop pushes nothing extra. */
  cfg: CfgStep;
}

/** history.state key carrying the snapshot (merged beside the router's fields). */
const KEY = 'rplNav';

function sameSnap(a: NavSnapshot, b: NavSnapshot): boolean {
  return a.step === b.step && a.tab === b.tab && a.cart === b.cart && a.cfg === b.cfg;
}

function readSnap(state: unknown): NavSnapshot | null {
  const raw = (state as Record<string, unknown> | null)?.[KEY] as NavSnapshot | undefined;
  return raw && typeof raw.step === 'number' && typeof raw.tab === 'string' ? raw : null;
}

/** Mount ONCE (OrderingFlow) — syncs browser history with the ordering flow. */
export function useBackNav(): void {
  const phase = useStore((s) => s.phase);
  const step = useStore((s) => s.step);
  const tab = useStore((s) => s.spTab);
  const cart = useStore((s) => s.spCartOpen);
  const cfg = useStore((s) => s.spCfgStep);

  /** Snapshot of the history entry we're currently ON. */
  const last = useRef<NavSnapshot | null>(null);
  /** The arrival snapshot — fallback when an entry's marker was stripped. */
  const base = useRef<NavSnapshot | null>(null);

  // Back/forward landed on an entry: apply its snapshot to the store. Setting
  // last FIRST makes the push effect below treat the change as already-synced
  // (no duplicate entry).
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      if (!base.current) return; // not stamped yet — a foreign pop, leave it be
      const snap = readSnap(e.state) ?? base.current;
      last.current = snap;
      const s = useStore.getState();
      if (s.step !== snap.step) s.setStep(snap.step);
      if (s.spTab !== snap.tab) s.setSpTab(snap.tab);
      if (s.spCartOpen !== snap.cart) s.setSpCartOpen(snap.cart);
      // Older entries predate `cfg` — leave the step alone rather than
      // snapping the customer back to 'size'.
      if (snap.cfg && s.spCfgStep !== snap.cfg) s.setSpCfgStep(snap.cfg);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Stamp the arrival entry once the flow is ready; push on every change the
  // customer makes after that. Pre-ready step jumps (resume restoring step 1)
  // never push — Back from the first visible screen leaves the site.
  useEffect(() => {
    if (phase !== 'ready') return;
    const snap: NavSnapshot = { step, tab, cart, cfg };
    try {
      if (!base.current) {
        base.current = snap;
        last.current = snap;
        history.replaceState({ ...(history.state ?? {}), [KEY]: snap }, '');
        return;
      }
      if (last.current && sameSnap(last.current, snap)) return;
      last.current = snap;
      history.pushState({ ...(history.state ?? {}), [KEY]: snap }, '');
    } catch {
      /* history API unavailable (tests/older webviews) — the flow still works */
    }
  }, [phase, step, tab, cart, cfg]);
}
